import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useReactTable, getCoreRowModel, flexRender, createColumnHelper } from '@tanstack/react-table';
import { TableBlock, getStatusLabel, getShipmentStatusLabel, formatUtcToLocalDateOnly } from '../../types';
import { Badge } from '../../../../components/ui/badge';
import { cn } from '../../../../lib/utils';

interface TableBlockRendererProps {
  block: TableBlock;
}

const columnHelper = createColumnHelper<Record<string, any>>();

export const TableBlockRenderer: React.FC<TableBlockRendererProps> = React.memo(({ block }) => {
  const { title = 'Shipments', columns: rawColumns, rows: rawRows, total_count } = block;

  const data = useMemo(() => {
    return rawRows.map((row) => {
      const rowObj: Record<string, any> = {};
      rawColumns.forEach((colKey, index) => {
        rowObj[colKey] = row[index];
      });
      return rowObj;
    });
  }, [rawColumns, rawRows]);

  const tableColumns = useMemo(() => {
    return rawColumns.map((colKey) => {
      const formatHeader = (key: string) => {
        if (key === 'cn_number') return 'CN Number';
        if (key === 'edd') return 'EDD';
        return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      };

      return columnHelper.accessor(colKey, {
        header: formatHeader(colKey),
        cell: (info) => {
          const value = info.getValue();
          if (value === null || value === undefined || value === '') {
            return <span className="text-gray-400">—</span>;
          }

          if (colKey === 'cn_number') {
            const cnStr = String(value);
            const rowData = info.row.original as Record<string, any> | undefined;
            const rowStatus = String(rowData?.status || '').toLowerCase();
            const canTrack = rowStatus !== 'delivered' && rowStatus !== 'local' && !rowData?.delivery_date;
            if (!canTrack) {
              return <span className="font-semibold text-gray-800">{cnStr}</span>;
            }
            return (
              <Link
                to={`/shipments/${cnStr}/track`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-semibold hover:underline inline-flex items-center gap-1"
                title={`Track shipment ${cnStr}`}
              >
                {cnStr}
              </Link>
            );
          }

          if (colKey === 'status') {
            const label = getStatusLabel(String(value));
            const isDelivered = String(value).toLowerCase() === 'delivered';
            const isTransit = String(value).toLowerCase() === 'in_transit';
            return (
              <Badge
                variant="outline"
                className={cn('text-xs font-medium border', isDelivered ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : isTransit ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200')}
              >
                {label}
              </Badge>
            );
          }

          if (colKey === 'shipment_status') {
            const label = getShipmentStatusLabel(String(value));
            const isHold = String(value).toLowerCase() === 'on_hold';
            const isSchedule = String(value).toLowerCase() === 'on_schedule';
            const isIncident = String(value).toLowerCase() === 'incident_reported';
            return (
              <Badge
                variant="outline"
                className={cn('text-xs font-medium border', isHold || isIncident ? 'bg-rose-50 text-rose-700 border-rose-200' : isSchedule ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-700 border-gray-200')}
              >
                {label}
              </Badge>
            );
          }

          if (colKey === 'edd' || colKey === 'delivery_date' || colKey === 'cn_date') {
            const formatted = formatUtcToLocalDateOnly(String(value));
            return <span>{formatted || String(value)}</span>;
          }

          return <span>{String(value)}</span>;
        },
      });
    });
  }, [rawColumns]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const rowCount = data.length;

  return (
    <div className="my-3 w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-gray-50/80 border-b border-gray-200 flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-800 uppercase tracking-wider">{title}</h4>
        <span className="text-xs text-gray-500 font-medium">Showing {rowCount} of {total_count}</span>
      </div>

      <div className="overflow-x-auto max-h-80 overflow-y-auto">
        <table className="w-full text-left border-collapse text-xs">
          <thead className="bg-gray-50 sticky top-0 border-b border-gray-200 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-100">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50/70 transition-colors">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});
